// 佐川計算（通常営業所）：便種コードで分類し、マスター単価 × 実績個数 で計上。
//
// ★ 2026-06-08 本城さん確定ルールを反映：
//   - 商業貨物(便種008・単価110等)・一般集荷(便種061・単価200) も「宅急便単価 × 実績個数」で計上
//     （以前は単価183/184/186のみ宅急便扱いで、110/200/クール等を取りこぼしていた）
//   - メール(便種004・単価40/50/60) は「ネコポス/メール単価 × 実績個数」
//   - 月額固定品目（便種104 荷捌 / 105 車両維持）は計上対象外
//   ※ 周南営業所だけは件数ベースの特殊計上（sagawaShunan.ts）。
//
// 業務構造：佐川CSVの「単数単価」は 佐川→HSD への請求単価。
// HSD→ドライバーへの支払いは マスター単価 を使う（差額が HSD のマージン）。
//
// ★ 便種→計上区分は便種マッピング（Issue #32）で判定。本城さんが設定画面で編集できる。
//    rules 省略時は DEFAULT_SAGAWA_BENKIND_RULES（従来挙動）を使う。

import type { Driver, SagawaRow, Statement, ItemRow, SagawaBenkindRule } from "../../types";
import { buildHeader, buildDeductions, eachDayOfMonth, summarize } from "./common";
import { classifySagawaCode, DEFAULT_SAGAWA_BENKIND_RULES } from "./sagawaRules";

const SAGAWA_OTHER_UNIT = 280;

export function calcSagawaStatement(
  driver: Driver,
  rows: SagawaRow[],
  targetMonth: string,
  rules: SagawaBenkindRule[] = DEFAULT_SAGAWA_BENKIND_RULES
): Statement {
  const days = eachDayOfMonth(targetMonth);
  const items: ItemRow[] = [];
  const warnings: string[] = [];

  // 日別集計用 Map
  const dailyTakkyu = new Map<string, number>();
  const dailyNekopos = new Map<string, number>();
  const dailyOther = new Map<string, number>();

  for (const row of rows) {
    if (!row.workDate.startsWith(targetMonth)) continue;
    const cat = classifySagawaCode(row.benkindCode, rules);
    if (cat === "takkyu") {
      dailyTakkyu.set(row.workDate, (dailyTakkyu.get(row.workDate) || 0) + row.countQty);
    } else if (cat === "nekopos") {
      dailyNekopos.set(row.workDate, (dailyNekopos.get(row.workDate) || 0) + row.countQty);
    } else if (cat === "other") {
      dailyOther.set(row.workDate, (dailyOther.get(row.workDate) || 0) + row.countQty);
    } else if (cat === "ignore") {
      // 月額(便種104/105)等は計上対象外
    } else if (row.tan === 350) {
      // ルール未設定だが単価350＝その他（後方互換）。未知の便種コードは取込時に #7 検証で弾く
      dailyOther.set(row.workDate, (dailyOther.get(row.workDate) || 0) + row.countQty);
    }
  }

  const uTakkyu = driver.unitTakkyu ?? 0;
  const uNeko = driver.unitNekopos ?? 0;
  if (uTakkyu === 0) warnings.push("マスター単価/宅急便 が未設定");
  if (uNeko === 0) warnings.push("マスター単価/ネコポス が未設定");

  // 宅配（旧「宅急便・EAZY」。#16でヤマト・佐川横断の共通名称に統一）
  for (const d of days) {
    const q = dailyTakkyu.get(d) || 0;
    items.push({
      deliveryDate: d,
      name: "宅配",
      unitPrice: uTakkyu,
      quantity: q,
      amount: q * uTakkyu,
      taxRate: "10%",
      category: "sales",
      carrier: "sagawa",
    });
  }
  // メール便（旧「ネコポス・クロネコDM便」。#16で共通名称に統一）
  for (const d of days) {
    const q = dailyNekopos.get(d) || 0;
    items.push({
      deliveryDate: d,
      name: "メール便",
      unitPrice: uNeko,
      quantity: q,
      amount: q * uNeko,
      taxRate: "10%",
      category: "sales",
      carrier: "sagawa",
    });
  }
  // その他
  for (const d of days) {
    const q = dailyOther.get(d) || 0;
    items.push({
      deliveryDate: d,
      name: "その他",
      unitPrice: SAGAWA_OTHER_UNIT,
      quantity: q,
      amount: q * SAGAWA_OTHER_UNIT,
      taxRate: "10%",
      category: "sales",
      carrier: "sagawa",
    });
  }

  // 控除（VBA S_Exception 相当）
  const header = buildHeader(driver, ["sagawa"], targetMonth);
  const deductions = buildDeductions(driver, header.salesDate);
  items.push(...deductions);

  const { salesTotal, deductTotal, netTotal } = summarize(items);

  return {
    ...header,
    items,
    salesTotal,
    deductTotal,
    netTotal,
    warnings,
    status: warnings.length > 0 ? "hold" : "draft",
  };
}
